import { Alert, Form } from "antd"
import { LoadingRegion } from "components/custom/Loader"
import { Skeleton } from "components/new-stuff/ui/Skeleton"
import { P1, P2 } from "components"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuthStore } from "stores"
import { companyService, COMPANY_ENDPOINTS } from "services/companyService"
import { onQueryError } from "utils/utils"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { ICompany } from "stores/company"
import { ExclamationCircleIcon } from "@heroicons/react/24/outline"
import FormSectionWrapper from "components/new-stuff/form/FormSectionWrapper"
import PrevNextButton from "components/new-stuff/form/PrevNextButton"
import { useEffect, useMemo, useState } from "react"
import { invalidateCompanySetupInfoQueries } from "pages/dashboard/wallets/integration/utils/invalidateCompanySetupInfoQueries"

interface Props {
  setActiveTab: React.Dispatch<React.SetStateAction<number>>
  onSuccess?: () => void
}

const StatutoryInfo = ({ setActiveTab, onSuccess }: Props) => {
  const [activeKey, setActiveKey] = useState<string[]>(["1"])
  const { user } = useAuthStore()

  const [form] = Form.useForm()

  const {
    data: companyRes,
    isLoading,
    isFetching,
  } = useQuery<IBaseRes<ICompany>>({
    queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
    queryFn: () => companyService.viewDetails(),
    cacheTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    onSuccess(res) {
      const data: any = res?.data
      form.setFieldsValue({
        companyType: data?.companyType?.company_type_id
          ? data?.companyType?.company_type_id?.toString()
          : "",
        tin: data?.tax_identification_number,
        pensionRefNo: data?.pension_reference_number,
        referenceNumber: data?.housing_reference_number,
      })
    },
  })
  const company = companyRes?.data

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="block"
            height="80px"
            className="w-full"
          />
        ))}
      </div>
    )
  }

  return (
    <DashboardWrapper>
      <div className="space-y-6">
        <TaxInfo
          isActive={activeKey.includes("1")}
          company={company}
          setActiveKey={setActiveKey}
          setActiveTab={setActiveTab}
        />

        <Pension
          isActive={activeKey.includes("2")}
          company={company}
          setActiveKey={setActiveKey}
          setActiveTab={setActiveTab}
        />

        <NHF
          isActive={activeKey.includes("3")}
          company={company}
          setActiveKey={setActiveKey}
          setActiveTab={setActiveTab}
          onSuccess={onSuccess}
        />

        {/* <CompanySignatories
          isActive={activeKey.includes("4")}
          company={company}
          setActiveKey={setActiveKey}
          setActiveTab={setActiveTab}
        /> */}
      </div>
    </DashboardWrapper>
  )
}

export default StatutoryInfo

interface CompProps {
  setActiveKey: React.Dispatch<React.SetStateAction<string[]>>
  isActive: boolean
  company?: ICompany | undefined
  setActiveTab: React.Dispatch<React.SetStateAction<number>>
  onSuccess?: () => void
}

const TaxInfo = ({
  isActive,
  company,
  setActiveKey,
  setActiveTab,
}: CompProps) => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { user } = useAuthStore()
  // const companyTypeValue = Form.useWatch("companyType", form)
  // const tinValue = Form.useWatch("tin", form)

  const { isLoading: isCompanyTypesLoading, data: companyTypesRes } = useQuery<
    IBaseRes<{ companyTypeId: number; description: string }[]>
  >({
    queryKey: [COMPANY_ENDPOINTS.GET_TYPES],
    queryFn: () => companyService.getCompanyTypes(),
    refetchOnWindowFocus: false,
  })
  const companyTypes = companyTypesRes?.data

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: (body: any) => companyService.editTaxDetails(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
      })
      mixPanelEvent("company-edit-tax-details-success")
      setActiveKey(["2"])
    },
    onError: onQueryError,
  })

  const onFinish = (values: any) => {
    const { companyType, tin } = values
    if (
      (!companyType && !tin) ||
      (company?.company_type_id === companyType &&
        company?.tax_identification_number === tin)
    ) {
      setActiveKey(["2"])
      return
    }
    submit({
      company_type_id: companyType || "",
      tax_identification_number: tin ? tin : "",
      name: user?.userOrganisation,
      au_comment: "",
    })
    mixPanelEvent("company-edit-pension-details-init")
  }

  return (
    <FormSectionWrapper
      name="taxForm"
      title="Tax setup"
      subtitle="Please make sure that your tax setup below is accurate."
      isActive={isActive}
      initialValues={{
        companyType:
          company?.company_type_id !== -1 && company?.company_type_id
            ? company?.company_type_id?.toString()
            : "",
        tin: company?.tax_identification_number,
      }}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{ maxWidth: 1024 }}
      autoComplete="off"
      footerBtns={
        <PrevNextButton
          onClickPrev={() => setActiveTab((prev) => prev - 1)}
          submitting={isSubmitting}
        />
      }
    >
      <LoadingRegion
        spinning={isCompanyTypesLoading}
        keepMounted
      >
        <div className="flex flex-col md:grid grid-cols-2 gap-x-10 px-4 md:px-6 xl:px-[60px]">
          <CustomSelect
            showSearch
            label="Company Type"
            name="companyType"
            required={true}
            isLoading={isCompanyTypesLoading}
            options={
              companyTypes?.map((companyType) => ({
                value: String(companyType.companyTypeId),
                label: companyType.description,
              })) || []
            }
            placeholder="Select Company Type"
          />

          <CustomInput
            label="Tax Identification number"
            name="tin"
            required={false}
            maxLength={20}
          />
          <div className="w-full">
            <P2 className="text-sm font-avenir text-padeSubtext">
              Filling schedule
            </P2>
            <P1 className="font-medium text-padeBlack font-avenir text-sm ">
              Monthly
            </P1>
          </div>
        </div>
      </LoadingRegion>
    </FormSectionWrapper>
  )
}

const Pension = ({ isActive, company, setActiveKey }: CompProps) => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { user } = useAuthStore()

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: (body: any) => companyService.editPensionDetails(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
      })
      mixPanelEvent("company-edit-pension-details-success")
      setActiveKey(["3"])
    },
    onError: onQueryError,
  })

  const onFinish = (values: any) => {
    const { pensionRefNo } = values

    if (!pensionRefNo || company?.pension_reference_number === pensionRefNo) {
      setActiveKey(["3"])
      return
    }

    submit({
      pension_reference_number: pensionRefNo,
      au_comment: "",
    })
    mixPanelEvent("company-edit-pension-details-init")
  }

  return (
    <FormSectionWrapper
      name="pensionForm"
      title="Pension setup"
      subtitle="Please make sure that your pension setup below is accurate if you want us to remit pension for you."
      isActive={isActive}
      initialValues={{
        pensionRefNo: company?.pension_reference_number,
      }}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{ maxWidth: 1024 }}
      autoComplete="off"
      footerBtns={
        <PrevNextButton
          submitting={isSubmitting}
          onClickPrev={() => {
            setActiveKey(["1"])
          }}
        />
      }
    >
      <div className="flex flex-col px-4 gap-6 md:px-6 xl:px-[60px]">
        <div className="bg-primary50 flex justify-start items-center gap-3 py-4 px-4">
          <ExclamationCircleIcon className="w-5 text-primary500" />
          <P2 className="text-neutral300">
            We won't remit your pension unless you ask us to.
          </P2>
        </div>

        <CustomInput
          label="Reference number"
          name="pensionRefNo"
        />
      </div>
    </FormSectionWrapper>
  )
}

const NHF = ({
  isActive,
  company,
  setActiveKey,
  setActiveTab,
  onSuccess,
}: CompProps) => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: (body: any) => companyService.editHousingDetails(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
      })
      mixPanelEvent("company-edit-housing-details-success")
      onSuccess?.()
    },
    onError: onQueryError,
  })

  const onFinish = (values: any) => {
    const { referenceNumber } = values

    if (
      !referenceNumber ||
      company?.housing_reference_number === referenceNumber
    ) {
      onSuccess?.()
      return
    }

    submit({
      housing_reference_number: referenceNumber,
      au_comment: "",
    })
    mixPanelEvent("company-edit-housing-details-init")
  }

  return (
    <FormSectionWrapper
      name="NHFForm"
      title="NHF Setup"
      subtitle="Please make sure that your NHF setup below is accurate if you want us to remit NHF for you."
      isActive={isActive}
      initialValues={{
        referenceNumber: company?.housing_reference_number,
      }}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{ maxWidth: 1024 }}
      autoComplete="off"
      footerBtns={
        <PrevNextButton
          submitting={isSubmitting}
          isEndOfForm={true}
          onClickPrev={() => {
            setActiveKey(["2"])
          }}
        />
      }
    >
      <div className="flex flex-col gap-6 px-4 md:px-6 xl:px-[60px]">
        <div className="bg-primary50 flex justify-start items-center gap-3 py-4 px-4">
          <ExclamationCircleIcon className="w-5 text-primary500" />
          <P2 className="text-neutral300">
            We won't remit your NHF unless you ask us to.
          </P2>
        </div>

        <CustomInput
          label="Reference Number"
          name="referenceNumber"
        />
      </div>
    </FormSectionWrapper>
  )
}

export const CompanySignatories = ({
  isActive,
  company,
  setActiveKey,
  setActiveTab,
}: CompProps) => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { user } = useAuthStore()

  const { data: companyDetailsRes } = useQuery<IBaseRes<ICompany>>({
    queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
    queryFn: () => companyService.viewDetails(),
    enabled: isActive,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const companyDetails = companyDetailsRes?.data

  const signatories = companyDetails?.signatories ?? []
  const existingPrimarySignatory =
    signatories.find((s) => s.is_primary) ?? signatories[0]

  useEffect(() => {
    if (!existingPrimarySignatory?.person_id) return
    if (form.isFieldsTouched(true)) return

    form.setFieldsValue({
      companySignatory: String(existingPrimarySignatory.person_id),
    })
  }, [existingPrimarySignatory?.person_id, form])

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: (body: any) => companyService.addSignatory(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
      })
      invalidateCompanySetupInfoQueries(queryClient)
      mixPanelEvent("company-add-signatory-success")
      setActiveKey(["3"])
    },
    onError: onQueryError,
  })

  const { isLoading: isPeopleLoading, data: peopleRes } = useQuery<
    IBaseRes<{ personId: number; fullName: string }[]>
  >({
    queryKey: [COMPANY_ENDPOINTS.GET_SIGNATORY_EMPLOYEES],
    queryFn: () => companyService.getSignatoryEmployees(),
    refetchOnWindowFocus: false,
  })
  const people = peopleRes?.data

  const peopleOptions = useMemo(() => {
    const raw =
      people?.map((person) => ({
        label: person.fullName,
        value: person.personId.toString(),
      })) ?? []

    const primaryId = existingPrimarySignatory?.person_id
      ? String(existingPrimarySignatory.person_id)
      : null

    if (!primaryId) return raw

    const hasPrimaryInOptions = raw.some((opt) => opt.value === primaryId)
    if (hasPrimaryInOptions) return raw

    return [
      {
        label:
          existingPrimarySignatory?.full_name ?? `Signatory (${primaryId})`,
        value: primaryId,
      },
      ...raw,
    ]
  }, [
    existingPrimarySignatory?.full_name,
    existingPrimarySignatory?.person_id,
    people,
  ])

  const onFinish = (values: any) => {
    const { companySignatory } = values

    if (!companySignatory) {
      setActiveKey(["3"])
    } else {
      submit({
        person_id: companySignatory,
      })
      mixPanelEvent("company-add-signatory-init")
    }
  }

  return (
    <FormSectionWrapper
      name="signatoryForm"
      title="Company Signatories"
      subtitle="The company signatories are responsible for electronically signing all documents created on PaidHR. When a new form is available to be e-signed, the Signatories are notified to electronically sign it online."
      isActive={isActive}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{ maxWidth: 1024 }}
      autoComplete="off"
      footerBtns={
        <PrevNextButton
          submitting={isSubmitting}
          onClickPrev={() => {
            setActiveKey(["1"])
          }}
        />
      }
    >
      <div className="flex flex-col px-4 md:px-6 xl:px-[60px]">
        {user?.isEmployeeSetup !== true && (
          <Alert
            message={
              "You need to add team members before you can add company signatories. You can add team members in the 'Add Team Members' section."
            }
            type="info"
            showIcon
            className="font-avenir text-neutral500 text-sm w-full flex justify-between items-center mb-4"
            style={{
              border: "none",
              backgroundColor: "#E8F0FE",
            }}
            icon={<ExclamationCircleIcon className="h-5 w-5 text-primary500" />}
          />
        )}

        <CustomSelect
          label="Company signatory"
          name="companySignatory"
          onSelectClick={() => {}}
          isLoading={isPeopleLoading}
          options={isPeopleLoading ? [] : peopleOptions}
          placeholder="Please select"
        />
      </div>
    </FormSectionWrapper>
  )
}
